import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  makeStyles,
} from '@material-ui/core';
import { fade } from '@material-ui/core/styles';
import AspectRatioSelector from './AspectRatioSelector';
import ProductUploadStrip from './ProductUploadStrip';
import { ImagePlusIcon } from './adsAgentIcons';
import { AD } from './adsAgentTokens';
import { AD_TYPE_OPTIONS, adTypeLabel } from './adTypeOptions';
import { MAX_PRODUCT_IMAGES } from './imageFiles';
import { ProductImage, PromptPart } from './adsGenerator.types';

// Port of whatsapp-console-frontend `src/pages/AdTemplates/StructuredPromptTemplate.js`
// without the video controls (Image/Video toggle and Duration) and without the
// Ads count stepper: the public page always generates 3 ads.

const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const chipHtml = (placeholder: string): string =>
  `<span data-chip="${escapeHtml(placeholder)}" class="prompt-chip" ` +
  `role="textbox" aria-label="${escapeHtml(placeholder)}"></span>`;

export const partsToHtml = (parts: PromptPart[]): string =>
  parts
    .map((part) =>
      typeof part === 'string' ? escapeHtml(part) : chipHtml(part.blank),
    )
    .join('');

// One source for the blank names, shared by the template and the message
// that reports them, so the two can't drift apart.
export const BLANKS = ['business name', 'website URL', 'target audience'];
const [BUSINESS, WEBSITE, AUDIENCE] = BLANKS;

export const DEFAULT_PARTS: PromptPart[] = [
  'Create a high-converting ad for ',
  { blank: BUSINESS },
  ' . This is my website URL ',
  { blank: WEBSITE },
  ' . My target audience are ',
  { blank: AUDIENCE },
  ' .',
];

const MAX_PROMPT_LENGTH = 2200;

// "a", "a and b", "a, b and c" — the message names every empty blank at once,
// so one click tells the user everything that is missing.
const joinWords = (words: string[]): string => {
  if (words.length <= 1) {
    return words[0] || '';
  }
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
};

const COMPACT = '@media (max-width: 1500px)';
const TIGHT = '@media (max-width: 1360px)';
const ULTRA = '@media (max-width: 1260px)';
const PHONE = '@media (max-width: 600px)';

const ctaTint = fade(AD.cta, 0.12);

const useStyles = makeStyles(() => ({
  card: {
    background: AD.surface,
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    boxSizing: 'border-box',
    overflow: 'hidden',
    fontFamily: AD.fontFamily,
  },

  promptArea: {
    padding: '0 0 18px',
    fontSize: 14,
    color: AD.text,
    fontWeight: 400,
    lineHeight: 2.2,
    outline: 'none',
    cursor: 'text',
    minHeight: 48,
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',

    '&:empty::before': {
      content:
        "'Describe your product, service or campaign… or paste a link to your website'",
      color: AD.textHint,
      pointerEvents: 'none',
    },

    '& .prompt-chip': {
      display: 'inline-block',
      padding: '2px 12px',
      borderRadius: 10,
      background: ctaTint,
      fontSize: 13.5,
      color: AD.primary,
      fontWeight: 500,
      lineHeight: '26px',
      verticalAlign: 'baseline',
      minWidth: 60,
      outline: 'none',
      cursor: 'text',
      transition: 'box-shadow 120ms, background 120ms',
    },
    '& .prompt-chip:empty::before': {
      content: 'attr(data-chip)',
      color: AD.textHint,
      pointerEvents: 'none',
    },
    '& .prompt-chip:focus': {
      background: AD.surface,
      boxShadow: `0 0 0 1px ${AD.primary}`,
    },
    '& .prompt-chip:not(:empty)': {
      background: ctaTint,
    },
  },

  promptViewport: {
    maxHeight: 160,
    overflowY: 'auto',
    padding: '22px 28px 0',
    [PHONE]: { padding: '18px 16px 0' },
  },

  sep: {
    height: 1,
    background: AD.borderSubtle,
  },

  optionsRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    minWidth: 0,
    padding: '9px 14px',
    gap: 16,
    [COMPACT]: { gap: 10, padding: '8px 10px' },
    [TIGHT]: { gap: 8, padding: '8px 6px' },
    [ULTRA]: { gap: 6, padding: '7px 5px' },
    [PHONE]: { flexWrap: 'wrap', rowGap: 10, padding: '10px 12px' },
  },

  sizeSlot: {
    display: 'inline-flex',
    alignItems: 'center',
    flex: '0 0 auto',
    '& > div': { width: 'auto' },
  },

  sizeControl: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
    userSelect: 'none',
    [COMPACT]: { gap: 5 },
    [TIGHT]: { gap: 4 },
    [ULTRA]: { gap: 3 },
  },
  optLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.4px',
    color: AD.textHint,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    [COMPACT]: { fontSize: 9.5, letterSpacing: '0.2px' },
    [TIGHT]: { fontSize: 9, letterSpacing: 0 },
    [ULTRA]: { display: 'none' },
    // Phones wrap the options row, so the labels fit and say what each icon does.
    [PHONE]: { display: 'inline' },
  },
  cardDragging: {
    boxShadow: `0 0 0 2px ${AD.primary}`,
  },
  adTypeControl: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 1,
    minWidth: 0,
    maxWidth: 260,
    cursor: 'pointer',
    userSelect: 'none',
    [COMPACT]: { gap: 5 },
    [TIGHT]: { gap: 4 },
    [ULTRA]: { gap: 3, maxWidth: 190 },
  },
  adTypeChevron: { flexShrink: 0 },
  adTypeValue: {
    fontSize: 14,
    fontWeight: 600,
    color: AD.text,
    whiteSpace: 'nowrap',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    [COMPACT]: { fontSize: 13 },
    [TIGHT]: { fontSize: 12 },
    [ULTRA]: { fontSize: 11.5 },
  },
  attachBtn: {
    height: 30,
    padding: '0 8px',
    borderRadius: 8,
    color: AD.text,
    '&:hover': { color: AD.primary, background: ctaTint },
    [COMPACT]: { padding: '0 5px' },
    [TIGHT]: { padding: '0 3px' },
    [ULTRA]: { padding: '0 2px', height: 26 },
  },
  attachLabel: {
    marginLeft: 6,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.4px',
    color: 'inherit',
    whiteSpace: 'nowrap',
    [COMPACT]: { marginLeft: 4, fontSize: 9.5, letterSpacing: '0.2px' },
    [TIGHT]: { fontSize: 9, letterSpacing: 0 },
    [ULTRA]: { marginLeft: 3 },
  },
  hiddenInput: { display: 'none' },
  adTypeOption: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 2,
    paddingTop: 6,
    paddingBottom: 6,
    fontFamily: AD.fontFamily,
  },
  adTypeLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: AD.text,
  },
  adTypeHint: {
    fontSize: 11,
    color: AD.textMuted,
  },
  sizeValue: {
    fontSize: 13,
    fontWeight: 600,
    color: AD.text,
    whiteSpace: 'nowrap',
    [COMPACT]: { fontSize: 12.5 },
    [TIGHT]: { fontSize: 12 },
    [ULTRA]: { fontSize: 11.5 },
  },



  spacer: { flex: 1 },

  '@keyframes adShimmer': {
    '0%': { transform: 'translateX(-100%)' },
    '100%': { transform: 'translateX(100%)' },
  },
  arrowBtn: {
    position: 'relative',
    overflow: 'hidden',
    height: 34,
    minWidth: 'auto',
    gap: 6,
    padding: '0 14px',
    borderRadius: AD.radius,
    background: AD.cta,
    border: 'none',
    boxShadow: AD.shadowNone,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'opacity 150ms',
    [COMPACT]: { height: 32, gap: 6, padding: '0 12px' },
    [TIGHT]: { height: 30, gap: 5, padding: '0 10px' },
    [ULTRA]: { height: 28, gap: 4, padding: '0 8px' },
    '&:hover:not(:disabled)': { opacity: 0.9 },
    '&:disabled': { background: AD.border, cursor: 'default' },

    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background:
        'linear-gradient(100deg, transparent 20%, rgba(255,255,255,0.28) 50%, transparent 80%)',
      animation: '$adShimmer 2.2s ease-in-out infinite',
      pointerEvents: 'none',
    },
    '&:disabled::after': { animation: 'none', background: 'none' },
    '@media (prefers-reduced-motion: reduce)': {
      '&::after': { animation: 'none' },
    },
  },
  arrowBtnBusy: {
    background: AD.cta,
    '&:disabled': { background: AD.cta, cursor: 'default' },
    '&:disabled::after': {
      animation: '$adShimmer 2.2s ease-in-out infinite',
      background:
        'linear-gradient(100deg, transparent 20%, rgba(255,255,255,0.28) 50%, transparent 80%)',
    },
  },
  arrowBtnLabel: {
    fontFamily: AD.fontFamily,
    fontSize: 13,
    fontWeight: 600,
    color: AD.onPrimary,
    whiteSpace: 'nowrap',
    maxWidth: '12ch',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    position: 'relative',
    zIndex: 1,
    [COMPACT]: { fontSize: 12 },
    [TIGHT]: { fontSize: 11.5 },
    [ULTRA]: { fontSize: 11 },
  },
  arrowBtnIcon: {
    display: 'inline-flex',
    position: 'relative',
    zIndex: 1,
  },
}));

const Chevron = ({ className }: { className?: string }): React.ReactElement => (
  <svg
    className={className}
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    stroke={AD.textMuted}
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M2 4l4 4 4-4" />
  </svg>
);

const StructuredPromptTemplate = (props: Props): React.ReactElement => {
  const {
    submitting,
    disabled,
    aspectRatio,
    adType,
    productImages,
    onAdTypeChange,
    onAddProductImages,
    onRemoveProductImage,
    onAspectRatioChange,
    onSubmit,
    onError,
  } = props;
  const classes = useStyles();
  const editorRef = useRef<HTMLDivElement>(null);
  const attachRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const [hasContent, setHasContent] = useState(true);
  const [adTypeAnchor, setAdTypeAnchor] = useState<HTMLElement | null>(null);
  const [dragging, setDragging] = useState(false);

  const locked = submitting || disabled;
  const canSubmit = !locked && hasContent;

  const imagesFrom = (list: FileList | null): File[] =>
    Array.from(list || []).filter((file) =>
      String(file.type || '').startsWith('image/'),
    );

  const handleAttachPick = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = imagesFrom(event.target.files);
    event.target.value = '';
    if (files.length) {
      onAddProductImages(files);
    }
  };

  const hasFiles = (event: React.DragEvent) =>
    Array.from(event.dataTransfer.types || []).includes('Files');

  const handleDragEnter = (event: React.DragEvent) => {
    if (!hasFiles(event)) {
      return;
    }
    dragDepth.current += 1;
    setDragging(true);
  };

  const handleDragLeave = () => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (!dragDepth.current) {
      setDragging(false);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    if (!hasFiles(event)) {
      return;
    }
    event.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    if (locked) {
      return;
    }
    const dropped = Array.from(event.dataTransfer.files || []);
    const files = imagesFrom(event.dataTransfer.files);
    if (!files.length) {
      if (dropped.length) {
        onError('Only image files can be added');
      }
      return;
    }
    onAddProductImages(files);
  };

  // Which blanks are still empty. Returns nothing when the chips are gone
  // entirely — deleting the template and writing a free-form prompt is a
  // supported path.
  const readMissing = useCallback((): string[] => {
    const editor = editorRef.current;
    if (!editor) {
      return [];
    }
    return Array.from(editor.querySelectorAll<HTMLElement>('.prompt-chip'))
      .filter((chip) => !(chip.textContent || '').trim())
      .map((chip) => chip.dataset.chip || '');
  }, []);

  const checkContent = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) {
      return;
    }
    const text = editor.innerText.replace(/\n/g, '').trim();
    if (text.length === 0) {
      editor.innerHTML = '';
    }
    setHasContent(text.length > 0);
  }, []);

  const caretToChip = useCallback((chip: HTMLElement) => {
    const range = document.createRange();
    range.selectNodeContents(chip);
    // Collapse to the end so tabbing into a filled blank appends rather than
    // silently replacing what's already typed there.
    range.collapse(false);
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }, []);

  useEffect(() => {
    if (editorRef.current && !editorRef.current.dataset.seeded) {
      editorRef.current.innerHTML = partsToHtml(DEFAULT_PARTS);
      editorRef.current.dataset.seeded = '1';
    }
  }, []);

  const handleBeforeInput = (event: React.FormEvent<HTMLDivElement>) => {
    const native = event.nativeEvent as InputEvent;
    if (native.inputType && native.inputType.startsWith('delete')) {
      return;
    }
    const currentLength = editorRef.current ? editorRef.current.innerText.length : 0;
    const insertedLength = native.data ? native.data.length : 0;
    if (currentLength + insertedLength > MAX_PROMPT_LENGTH) {
      event.preventDefault();
    }
  };

  const handleGenerate = useCallback(() => {
    if (locked || !editorRef.current) {
      return;
    }
    // Read the chips now rather than trusting state: this is the moment the
    // answer has to be right, and it names every empty blank at once.
    const blanks = readMissing();
    if (blanks.length) {
      onError(`Add your ${joinWords(blanks)}`);
      return;
    }
    const raw = editorRef.current.innerText.trim();
    if (raw.length < 3) {
      onError('Please describe your business to generate ads');
      return;
    }
    onSubmit(raw);
  }, [locked, readMissing, onError, onSubmit]);

  // The chips are spans inside one contentEditable region, not focusable
  // elements, so Tab has to move the caret between them rather than move focus.
  const chipAtCaret = (): HTMLElement | null => {
    const selection = window.getSelection();
    const anchor = selection && selection.anchorNode;
    if (!anchor || !editorRef.current) {
      return null;
    }
    const el = (
      anchor.nodeType === Node.ELEMENT_NODE ? anchor : anchor.parentElement
    ) as HTMLElement | null;
    if (!el || !editorRef.current.contains(el)) {
      return null;
    }
    return el.closest('.prompt-chip');
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleGenerate();
      return;
    }
    if (event.key === 'Tab') {
      const chips = Array.from(
        editorRef.current
          ? editorRef.current.querySelectorAll<HTMLElement>('.prompt-chip')
          : [],
      );
      if (!chips.length) {
        return;
      }
      const current = chipAtCaret();
      const index = current ? chips.indexOf(current) : -1;
      const next = event.shiftKey ? index - 1 : index + 1;
      // Past either end, let Tab do its normal thing and leave the editor.
      if (next < 0 || next >= chips.length) {
        return;
      }
      event.preventDefault();
      caretToChip(chips[next]);
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    const text = event.clipboardData.getData('text/plain');
    const currentLength = editorRef.current ? editorRef.current.innerText.length : 0;
    const remaining = Math.max(0, MAX_PROMPT_LENGTH - currentLength);
    if (remaining > 0) {
      document.execCommand('insertText', false, text.slice(0, remaining));
    }
  };


  return (
    <div
      className={`${classes.card} ${dragging ? classes.cardDragging : ''}`}
      onDragEnter={handleDragEnter}
      onDragOver={(event) => {
        if (hasFiles(event)) {
          event.preventDefault();
        }
      }}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <ProductUploadStrip items={productImages} onRemove={onRemoveProductImage} />

      <div className={classes.promptViewport}>
        <div
          ref={editorRef}
          className={classes.promptArea}
          contentEditable={!locked}
          suppressContentEditableWarning
          onInput={checkContent}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBeforeInput={handleBeforeInput}
          role="textbox"
          aria-label="Ad prompt"
          aria-placeholder="Describe your product, service or campaign…"
          spellCheck
        />
      </div>

      <div className={classes.sep} />

      <div className={classes.optionsRow}>
        <Tooltip
          title={
            productImages.length >= MAX_PRODUCT_IMAGES
              ? `You can add up to ${MAX_PRODUCT_IMAGES} product images`
              : 'Add product images'
          }
        >
          <span>
            <IconButton
              className={classes.attachBtn}
              size="small"
              disabled={locked || productImages.length >= MAX_PRODUCT_IMAGES}
              onClick={() => attachRef.current && attachRef.current.click()}
              aria-label="Add product images"
            >
              <ImagePlusIcon size={18} />
              <span className={classes.attachLabel}>Product</span>
            </IconButton>
          </span>
        </Tooltip>

        <input
          ref={attachRef}
          className={classes.hiddenInput}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          onChange={handleAttachPick}
        />

        <div
          className={classes.adTypeControl}
          role="button"
          aria-label={`Ad type: ${adTypeLabel(adType)}`}
          onClick={(event) => !locked && setAdTypeAnchor(event.currentTarget)}
        >
          <span className={classes.optLabel}>Ad Type</span>
          <span className={classes.adTypeValue}>{adTypeLabel(adType)}</span>
          <Chevron className={classes.adTypeChevron} />
        </div>

        <Menu
          anchorEl={adTypeAnchor}
          open={Boolean(adTypeAnchor)}
          onClose={() => setAdTypeAnchor(null)}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {AD_TYPE_OPTIONS.map((option) => (
            <MenuItem
              key={option.value}
              selected={option.value === adType}
              className={classes.adTypeOption}
              onClick={() => {
                onAdTypeChange(option.value);
                setAdTypeAnchor(null);
              }}
            >
              <span className={classes.adTypeLabel}>{option.label}</span>
              {option.description ? (
                <span className={classes.adTypeHint}>{option.description}</span>
              ) : null}
            </MenuItem>
          ))}
        </Menu>

        <div className={classes.sizeSlot}>
          <AspectRatioSelector
            currentRatio={aspectRatio}
            onRatioChange={onAspectRatioChange}
            previewSize={38}
          >
            <div
              className={classes.sizeControl}
              role="button"
              aria-label={`Image size: ${aspectRatio}`}
            >
              <span className={classes.optLabel}>Size</span>
              <span className={classes.sizeValue}>{aspectRatio}</span>
              <Chevron />
            </div>
          </AspectRatioSelector>
        </div>


        <div className={classes.spacer} />

        <button
          className={`${classes.arrowBtn} ${locked ? classes.arrowBtnBusy : ''}`}
          disabled={!canSubmit}
          onClick={handleGenerate}
          type="button"
          aria-label={locked ? 'Generating…' : 'Generate ad'}
          aria-live={locked ? 'polite' : undefined}
        >
          {submitting ? (
            <>
              <span className={classes.arrowBtnIcon}>
                <CircularProgress size={15} style={{ color: AD.onPrimary }} />
              </span>
              <span className={classes.arrowBtnLabel}>Generating…</span>
            </>
          ) : (
            <>
              <span className={classes.arrowBtnIcon}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke={AD.onPrimary}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 10h12M11 5l5 5-5 5" />
                </svg>
              </span>
              <span className={classes.arrowBtnLabel}>Generate Ad</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

interface Props {
  submitting: boolean;
  disabled: boolean;
  aspectRatio: string;
  adType: string;
  productImages: ProductImage[];
  onAdTypeChange: (value: string) => void;
  onAddProductImages: (files: File[]) => void;
  onRemoveProductImage: (id: string) => void;
  onAspectRatioChange: (value: string) => void;
  onSubmit: (prompt: string) => void;
  onError: (message: string) => void;
}

export default StructuredPromptTemplate;
